var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// import { diceGroup } from '../interface/gameController';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import globalStore from '../store';
import DiceManipulative from './DiceManipulative';
import NumberAndMoneyPieceManipulative from './NumberAndMoneyPieceManipulative';
import PatternShapesManipulative from './PatternShapesManipulative';
import { DataContext } from '../player/DataContext';
import '../styles/widgets/manipulativeGroup.scss';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { useDuplicateAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import { useTranslation } from 'react-i18next';
import { getImageSrc } from '../helper/imageBlobCache';
import { nmpAnimationTicker } from '../helper/nmpAnimationTicker';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
// Reliable touch-primary detection — works on modern iPads (desktop UA)
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var GROUP_PADDING = 20;
var GROUP_GAP = 20; // matches CSS gap: 20px on .manipulativeGroup
// A pure dice group wraps after this many dice per row, so a group of more than
// 3 dice flows onto the next row instead of stretching into one long line.
var DICE_PER_ROW = 3;
// Max items allowed per row. Only dice groups are capped; other manipulatives
// keep the width-based wrapping. Returns Infinity (no cap) for non-dice groups.
//
// A dice pool authored with `center.vertical` overrides the default cap: the
// flag is propagated onto each die's `data.vertical` at merge time, so a fully
// vertical pool wraps after 1 die (single column) and a fully horizontal pool
// never wraps (single row). Mixed/absent → the default ~square grid (3/row).
var getMaxItemsPerRow = function (items) {
    if (items.length === 0 || !items.every(function (item) { return item.type === 'dice'; }))
        return Infinity;
    if (items.every(function (item) { var _a; return ((_a = item.data) === null || _a === void 0 ? void 0 : _a.vertical) === true; }))
        return 1;
    if (items.every(function (item) { var _a; return ((_a = item.data) === null || _a === void 0 ? void 0 : _a.vertical) === false; }))
        return Infinity;
    return DICE_PER_ROW;
};
var ManipulativeGroup = function (props) {
    var _a;
    var _b, _c, _d;
    var id = props.id, data = props.data;
    var t = useTranslation().t;
    var duplicateLabel = useDuplicateAnnounce(id, t('text.group'));
    /** ---------------- GLOBAL CONFIG ---------------- */
    var _e = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _e.zoomLevel, canvasWidth = _e.canvasWidth, canvasHeight = _e.canvasHeight;
    /** ---------------- DATA STORE FROM GAME CONTROLLER ---------------- */
    var _f = useContext(DataContext), selectedTool = _f.selectedTool, setSelectedTool = _f.setSelectedTool, setManipulative = _f.setManipulative, panningEnable = _f.panningEnable, manipulative = _f.manipulative, setContextMenu = _f.setContextMenu;
    var _g = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _g.startMultiDrag, moveMultiDrag = _g.moveMultiDrag, endMultiDrag = _g.endMultiDrag;
    var groupDragStartPosRef = useRef(null);
    var getMinXY = function (data) {
        if (!data.length)
            return { x: 0, y: 0, item: undefined };
        // Prefer groupCenter — it's stable across break/exchange/link because new
        // pieces inherit it from their parent. Using individual piece `center`
        // would cause the wrapper to jump since new pieces have offset-calculated centers.
        var gc = data[0].data.groupCenter;
        if (gc)
            return { x: gc.x, y: gc.y, item: data[0].data };
        // Fallback for initial group creation before groupCenter is set
        var minItem = data[0].data;
        for (var i = 1; i < data.length; i++) {
            var item = data[i].data;
            if (item.center.x < minItem.center.x) {
                minItem = item;
            }
        }
        return {
            x: minItem.center.x - GROUP_PADDING,
            y: minItem.center.y - GROUP_PADDING,
            item: minItem,
        };
    };
    /** ---------------- STATE ---------------- */
    var _h = useState([]), typeString = _h[0], setTypeString = _h[1];
    var _j = useState(false), isDragging = _j[0], setIsDragging = _j[1];
    var isDraggingRef = useRef(false);
    var _k = useState(null), dragStart = _k[0], setDragStart = _k[1];
    var _l = useState([]), groupStartPos = _l[0], setGroupStartPos = _l[1];
    var _m = useState(getMinXY(data)), groupPos = _m[0], setGroupPos = _m[1];
    var _o = useState(false), isFocused = _o[0], setIsFocused = _o[1];
    var _p = useState((_d = (_c = (_b = data[0]) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.groupScale) !== null && _d !== void 0 ? _d : 1), groupScale = _p[0], setGroupScale = _p[1];
    // Mirrors nmpAnimationTicker so the in-group NMP visibility gate (see render
    // below) can ignore stale `animatingOut: true` flags when no animation is
    // actually in flight. Without this, a piece left with a leaked `animatingOut`
    // (e.g. an interrupted exchange) would be permanently hidden whenever
    // another sibling is active — that's the "3 of 4 nickels vanish on drag"
    // bug since the wrapper re-evaluates the gate per frame.
    var _q = useState(nmpAnimationTicker.isAnimating), isNmpAnimating = _q[0], setIsNmpAnimating = _q[1];
    useEffect(function () {
        return nmpAnimationTicker.subscribe(function () {
            setIsNmpAnimating(nmpAnimationTicker.isAnimating);
        });
    }, []);
    // Check if this is an outline group (all members have positioning locked)
    var isOutlineGroup = useMemo(function () {
        return data.length > 0 && data.every(function (item) { var _a, _b; return ((_b = (_a = item.data) === null || _a === void 0 ? void 0 : _a.lock) === null || _b === void 0 ? void 0 : _b.positioning) === true; });
    }, [data]);
    // Pick a screen-reader-friendly label for the group based on its contents.
    // Falls back to the generic "Select Group" when members are mixed.
    var groupLabel = useMemo(function () {
        if (data.length > 0 && data.every(function (item) { var _a; return ((_a = item.data) === null || _a === void 0 ? void 0 : _a.type) === 'patternShapes'; })) {
            return t('text.patternBlockGroup');
        }
        return t('text.selectGroup');
    }, [data, t]);
    /** ---------------- REF ---------------- */
    var minXYRef = useRef(getMinXY(data));
    var dragStartRef = useRef(null);
    var dragOffsetRef = useRef(null);
    var hasMoved = useRef(false);
    var dragRafRef = useRef(0);
    var wasRightClick = useRef(false);
    // Touch devices: deferred roll timer so SelectFocusOverlay click can cancel it
    var deferredRollTimer = useRef(null);
    var manipulativeRef = useRef({});
    var wrapperOuterRef = useRef(null);
    var keyMoveStartRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    // Rotation refs for group-level rotate interaction
    var rotatingRef = useRef(false);
    var rotateStartAngleRef = useRef(0);
    var initialCentersRef = useRef({});
    var initialRotationsRef = useRef({});
    var oldGroupSnapshotRef = useRef([]);
    /** ---------------- GROUP AUTO-POSITIONING INSIDE WRAPPER ---------------- */
    var calcAbsolutePosition = function (groupCenter, groupScaleNum) {
        var xPositionArray = [];
        var yPositionArray = [];
        var maxW = canvasWidth / 2;
        var startX = groupCenter.x + GROUP_PADDING;
        var _nextX = startX;
        var _currentY = groupCenter.y + GROUP_PADDING;
        var rowMaxH = 0;
        var rowW = 0;
        var widestRow = 0;
        var colCount = 0;
        var maxPerRow = getMaxItemsPerRow(data);
        data.forEach(function (_item, idx) {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            var itemW = ((_c = (_b = (_a = _item.data) === null || _a === void 0 ? void 0 : _a.defaultDimensions) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : 1) *
                ((_d = _item.data.beforeGroupScale) !== null && _d !== void 0 ? _d : 1) *
                (groupScaleNum !== null && groupScaleNum !== void 0 ? groupScaleNum : 1);
            var itemH = ((_g = (_f = (_e = _item.data) === null || _e === void 0 ? void 0 : _e.defaultDimensions) === null || _f === void 0 ? void 0 : _f.height) !== null && _g !== void 0 ? _g : 1) *
                ((_h = _item.data.beforeGroupScale) !== null && _h !== void 0 ? _h : 1) *
                (groupScaleNum !== null && groupScaleNum !== void 0 ? groupScaleNum : 1);
            // Wrap to next row if this item would exceed max width OR the per-row item
            // cap (dice groups: 3 per row). Skip for the first item in a row.
            if (idx > 0 && (_nextX + itemW - startX > maxW || colCount >= maxPerRow)) {
                widestRow = Math.max(widestRow, rowW);
                _currentY += rowMaxH + GROUP_GAP;
                _nextX = startX;
                rowMaxH = 0;
                rowW = 0;
                colCount = 0;
            }
            xPositionArray.push(_nextX);
            yPositionArray.push(_currentY);
            rowMaxH = Math.max(rowMaxH, itemH);
            var gap = idx < data.length - 1 ? GROUP_GAP : 0;
            rowW += itemW + gap;
            _nextX += itemW + gap;
            colCount++;
        });
        widestRow = Math.max(widestRow, rowW);
        return {
            x: xPositionArray,
            y: yPositionArray,
            contentWidth: widestRow,
        };
    };
    /** ---------------- GROUP ROTATE HANDLERS ---------------- */
    var onRotateStart = function (e) {
        e.stopPropagation();
        if (panningEnable || isOutlineGroup)
            return;
        var clientX = e.clientX;
        var clientY = e.clientY;
        // compute group center in canvas coords
        var centerX = groupPos.x + (groupDims.width || 0) / 2;
        var centerY = groupPos.y + (groupDims.height || 0) / 2;
        rotateStartAngleRef.current = Math.atan2(clientY - centerY, clientX - centerX);
        // capture initial centers and rotations (absolute canvas coords)
        var members = manipulative.filter(function (m) { return m.data.groupId === id; });
        initialCentersRef.current = {};
        initialRotationsRef.current = {};
        oldGroupSnapshotRef.current = JSON.parse(JSON.stringify(members));
        members.forEach(function (item) {
            var _a;
            if (!item.data.id)
                return;
            initialCentersRef.current[item.data.id] = {
                x: item.data.center.x,
                y: item.data.center.y,
            };
            initialRotationsRef.current[item.data.id] = (_a = item.data.rotation) !== null && _a !== void 0 ? _a : 0;
        });
        rotatingRef.current = true;
        var onMove = function (me) {
            if (!rotatingRef.current)
                return;
            var curA = Math.atan2(me.clientY - centerY, me.clientX - centerX);
            var delta = curA - rotateStartAngleRef.current; // radians
            var degDelta = (delta * 180) / Math.PI;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a, _b;
                    if (item.data.groupId !== id)
                        return item;
                    var iid = (_a = item.data.id) !== null && _a !== void 0 ? _a : '';
                    var init = initialCentersRef.current[iid];
                    var rot = (_b = initialRotationsRef.current[iid]) !== null && _b !== void 0 ? _b : 0;
                    if (!init)
                        return item;
                    var dx = init.x - centerX;
                    var dy = init.y - centerY;
                    var cos = Math.cos(delta);
                    var sin = Math.sin(delta);
                    var nx = centerX + dx * cos - dy * sin;
                    var ny = centerY + dx * sin + dy * cos;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: nx, y: ny }, rotation: rot + degDelta }) });
                });
            });
        };
        var onUp = function (me) {
            if (!rotatingRef.current)
                return;
            rotatingRef.current = false;
            // finalize and save states per member
            var newMembers = manipulative.filter(function (m) { return m.data.groupId === id; });
            oldGroupSnapshotRef.current.forEach(function (oldItem) {
                var updated = newMembers.find(function (n) { return n.data.id === oldItem.data.id; });
                if (!oldItem.data.id || !updated)
                    return;
                saveState({
                    type: oldItem.data.id,
                    id: oldItem.data.id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldItem),
                    new: JSON.stringify(updated),
                });
            });
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    /** ---------------- DRAG START ---------------- */
    var handleMouseDown = function (e) {
        // Don't allow interaction with outline groups
        // if (isOutlineGroup) {
        //   e.preventDefault();
        //   e.stopPropagation();
        //   return;
        // }
        // Prevent default touch behavior to avoid page refresh/scrolling on iPad
        if ('touches' in e) {
            e.preventDefault();
        }
        // Track right-click so stopDrag doesn't roll dice
        wasRightClick.current = 'button' in e && e.button === 2;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (!panningEnable) {
            // Select the group on click/drag if not already focused
            // if (!isFocused) {
            //   setSelectedTool(selectTool(e as unknown as MouseEvent, id, selectedTool, false));
            // }
            setIsDragging(true);
            isDraggingRef.current = true;
            dragStartRef.current = {
                x: clientX,
                y: clientY,
            };
            hasMoved.current = false;
            var dragOffset = {
                x: clientX - groupPos.x * zoomLevel,
                y: clientY - groupPos.y * zoomLevel,
            };
            dragOffsetRef.current = dragOffset;
            setDragStart(dragOffset);
            groupDragStartPosRef.current = { x: groupPos.x, y: groupPos.y };
            var _a = setStatesInManipulative({ x: groupPos.x, y: groupPos.y }, groupScale), oldItem = _a.oldItem, newItem = _a.newItem;
            setGroupStartPos(oldItem);
            startMultiDrag();
        }
    };
    /** ---------------- GROUP INTERACTIVE REF (for keyboard focus) ---------------- */
    var groupInteractiveRef = useRef(null);
    /** ---------------- ACCESSIBLE GROUP LABEL ---------------- */
    var hasDice = data.some(function (item) { return item.type === 'dice' || item.type === 'dice_pool'; });
    var groupNumber = useMemo(function () {
        var groupIds = [];
        manipulative.forEach(function (m) {
            var gid = m.data.groupId;
            if (gid && !groupIds.includes(gid))
                groupIds.push(gid);
        });
        return groupIds.indexOf(id) + 1;
    }, [manipulative, id]);
    var groupAriaLabel = useMemo(function () {
        if (duplicateLabel)
            return duplicateLabel;
        var typeCount = {};
        data.forEach(function (item) {
            var type = item.type;
            typeCount[type] = (typeCount[type] || 0) + 1;
        });
        var parts = Object.entries(typeCount).map(function (_a) {
            var type = _a[0], count = _a[1];
            switch (type) {
                case 'dice':
                    return "".concat(count, " ").concat(t('text.dice'));
                case 'counter':
                    return t(count === 1 ? 'text.counterItem' : 'text.counterItems', { count: count });
                case 'numberPiece':
                    return t(count === 1 ? 'text.numberPieceItem' : 'text.numberPieceItems', { count: count });
                case 'moneyPiece':
                    return t(count === 1 ? 'text.moneyPieceItem' : 'text.moneyPieceItems', { count: count });
                case 'patternShapes':
                    return t(count === 1 ? 'text.patternShapeItem' : 'text.patternShapeItems', { count: count });
                default:
                    return t(count === 1 ? 'text.genericItem' : 'text.genericItems', { count: count });
            }
        });
        var label = t('text.groupLabel', { group: t('text.group'), number: groupNumber, parts: parts.join(', ') });
        if (hasDice) {
            label = t('text.groupPressToRoll', { label: label });
        }
        return label;
    }, [data, t, hasDice, isOutlineGroup, groupNumber, duplicateLabel]);
    /** ---------------- SCREEN READER ANNOUNCEMENT ---------------- */
    var _r = useState(''), announceText = _r[0], setAnnounceText = _r[1];
    /** ---------------- INIT ---------------- */
    useEffect(function () {
        setTypeString(Array.from(data.map(function (item) { return "".concat(item.data.id, ":group"); })));
    }, [data.length]);
    /** ---------------- SETTING MINIMUM GROUP POSITION ---------------- */
    var minXY = useMemo(function () {
        var result = getMinXY(data);
        // Return stable reference if value unchanged — prevents useLayoutEffect
        // from re-firing after setStatesInManipulative updates piece centers.
        if (result.x === minXYRef.current.x && result.y === minXYRef.current.y) {
            return minXYRef.current;
        }
        minXYRef.current = result;
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        JSON.stringify(data.map(function (item) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            return ({
                cx: (_b = (_a = item.data.center) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0,
                cy: (_d = (_c = item.data.center) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0,
                dw: (_f = (_e = item.data.defaultDimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : 0,
                dh: (_h = (_g = item.data.defaultDimensions) === null || _g === void 0 ? void 0 : _g.height) !== null && _h !== void 0 ? _h : 0,
                bs: (_j = item.data.beforeGroupScale) !== null && _j !== void 0 ? _j : 0,
                gs: (_k = item.data.groupScale) !== null && _k !== void 0 ? _k : 0,
                gcx: (_m = (_l = item.data.groupCenter) === null || _l === void 0 ? void 0 : _l.x) !== null && _m !== void 0 ? _m : 0,
                gcy: (_p = (_o = item.data.groupCenter) === null || _o === void 0 ? void 0 : _o.y) !== null && _p !== void 0 ? _p : 0,
            });
        })),
    ]);
    var derivedGroupScale = useMemo(function () { var _a, _b, _c; return (_c = (_b = (_a = data[0]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.groupScale) !== null && _c !== void 0 ? _c : 1; }, [data]);
    // Sync local groupScale from props (for undo/redo)
    useEffect(function () {
        setGroupScale(derivedGroupScale);
    }, [derivedGroupScale]);
    useLayoutEffect(function () {
        if (isDraggingRef.current || rotatingRef.current)
            return;
        var raw = minXY;
        var clampedX = raw.x;
        var clampedY = raw.y;
        if (wrapperOuterRef.current) {
            var groupW = wrapperOuterRef.current.offsetWidth;
            var groupH = wrapperOuterRef.current.offsetHeight;
            clampedX = Math.max(0, Math.min(raw.x, canvasWidth - groupW));
            clampedY = Math.max(0, Math.min(raw.y, canvasHeight - groupH));
        }
        var _minmax = __assign(__assign({}, raw), { x: clampedX, y: clampedY });
        setGroupPos(_minmax);
        setStatesInManipulative({ x: _minmax.x, y: _minmax.y }, derivedGroupScale);
    }, [minXY, derivedGroupScale, isDragging]);
    var setStatesInManipulative = function (groupPosState, groupScaleState) {
        var _a = calcAbsolutePosition(groupPosState, groupScaleState), xPositionArray = _a.x, yPositionArray = _a.y;
        // Compute old/new items synchronously from closure for undo/redo
        var oldItem = [];
        var newItem = [];
        var fallbackX = groupPosState.x + GROUP_PADDING;
        var fallbackY = groupPosState.y + GROUP_PADDING;
        manipulative.forEach(function (item) {
            var _a, _b, _c;
            if (item.data.groupId === id) {
                oldItem.push(item);
                var _pos = (_a = item.data.groupPosition) !== null && _a !== void 0 ? _a : 0;
                newItem.push(__assign(__assign({}, item), { data: __assign(__assign({}, item.data), { groupCenter: { x: groupPosState.x, y: groupPosState.y }, groupScale: groupScaleState, center: {
                            x: (_b = xPositionArray[_pos]) !== null && _b !== void 0 ? _b : fallbackX,
                            y: (_c = yPositionArray[_pos]) !== null && _c !== void 0 ? _c : fallbackY,
                        } }) }));
            }
        });
        // Use functional updater so concurrent group updates chain correctly
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var _a, _b, _c;
                if (item.data.groupId === id) {
                    var _pos = (_a = item.data.groupPosition) !== null && _a !== void 0 ? _a : 0;
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { groupCenter: { x: groupPosState.x, y: groupPosState.y }, groupScale: groupScaleState, center: {
                                x: (_b = xPositionArray[_pos]) !== null && _b !== void 0 ? _b : fallbackX,
                                y: (_c = yPositionArray[_pos]) !== null && _c !== void 0 ? _c : fallbackY,
                            } }) });
                }
                return item;
            });
        });
        return { oldItem: oldItem, newItem: newItem };
    };
    /** ---------------- CALCULATING INDIVIDUAL DICE POSITION ---------------- */
    useLayoutEffect(function () {
    }, [JSON.stringify(groupPos), groupScale]);
    /** ---------------- IS FOCUSED ---------------- */
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        // Auto-focus the group wrapper when newly selected (e.g. via SelectFocusOverlay click)
        if (nowFocused && !wasFocused && groupInteractiveRef.current) {
            // Use 100ms timeout so this fires after closeMenu's 50ms focus-restore,
            // which would otherwise steal focus back to the original shape after grouping.
            setTimeout(function () {
                var _a, _b;
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = groupInteractiveRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [typeString, selectedTool]);
    /** ---------------- DRAG MOVE & END ---------------- */
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b, _c, _d, _e, _f;
            // Prevent default touch behavior to avoid page refresh/scrolling on iPad
            if ('touches' in e) {
                e.preventDefault();
            }
            if (!dragStartRef.current)
                return;
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            // movement threshold
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (isOutlineGroup) {
                hasMoved.current = false;
                return;
            }
            if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMoved.current = true;
            }
            if (!hasMoved.current)
                return;
            var offset = dragOffsetRef.current;
            var rawGX = (clientX - ((_a = offset === null || offset === void 0 ? void 0 : offset.x) !== null && _a !== void 0 ? _a : 0)) / zoomLevel;
            var rawGY = (clientY - ((_b = offset === null || offset === void 0 ? void 0 : offset.y) !== null && _b !== void 0 ? _b : 0)) / zoomLevel;
            var groupW = (_d = (_c = wrapperOuterRef.current) === null || _c === void 0 ? void 0 : _c.offsetWidth) !== null && _d !== void 0 ? _d : 0;
            var groupH = (_f = (_e = wrapperOuterRef.current) === null || _e === void 0 ? void 0 : _e.offsetHeight) !== null && _f !== void 0 ? _f : 0;
            var _groupXpos = Math.max(0, Math.min(rawGX, canvasWidth - groupW));
            var _groupYpos = Math.max(0, Math.min(rawGY, canvasHeight - groupH));
            setGroupPos(function (prev) { return (__assign(__assign({}, prev), { x: _groupXpos, y: _groupYpos })); });
            if (groupDragStartPosRef.current) {
                moveMultiDrag(_groupXpos - groupDragStartPosRef.current.x, _groupYpos - groupDragStartPosRef.current.y);
            }
            // Throttle context update to once per animation frame
            if (dragRafRef.current)
                cancelAnimationFrame(dragRafRef.current);
            dragRafRef.current = requestAnimationFrame(function () {
                dragRafRef.current = 0;
                setStatesInManipulative({ x: _groupXpos, y: _groupYpos }, groupScale);
            });
        };
        var stopDrag = function () {
            if (!hasMoved.current && !wasRightClick.current) {
                var doRoll = function () {
                    var oldDiceGroup = [];
                    var newDiceGroup = [];
                    Object.entries(manipulativeRef.current).forEach(function (item) {
                        var _key = item[0], value = item[1];
                        if (_key.includes('dice')) {
                            value === null || value === void 0 ? void 0 : value.rollDice(function (_a) {
                                var oldDiceItem = _a.oldDiceItem, newDiceItem = _a.newDiceItem;
                                if (oldDiceItem && newDiceItem) {
                                    oldDiceGroup.push(oldDiceItem);
                                    newDiceGroup.push(newDiceItem);
                                }
                            });
                        }
                    });
                    // Apply all dice roll results in a single setManipulative call
                    if (newDiceGroup.length > 0) {
                        var newFaceMap_1 = new Map();
                        newDiceGroup.forEach(function (item) {
                            if (item.data.id)
                                newFaceMap_1.set(item.data.id, item);
                        });
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                var _a;
                                var updated = newFaceMap_1.get((_a = item.data.id) !== null && _a !== void 0 ? _a : '');
                                return updated !== null && updated !== void 0 ? updated : item;
                            });
                        });
                    }
                    if (oldDiceGroup.length > 0 && newDiceGroup.length > 0) {
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'completeData',
                            old: JSON.stringify(oldDiceGroup),
                            new: JSON.stringify(newDiceGroup),
                        });
                        // Clear immediately; set in timeout so '' → text is always a state change
                        setAnnounceText('');
                        setTimeout(function () {
                            var results = newDiceGroup.map(function (item) {
                                var _a, _b, _c, _d, _e, _f;
                                var d = item.data;
                                if (d.diceType === 'dot') {
                                    return "face ".concat(d.activeFaceIndex, " of ").concat((_b = (_a = d.faces) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 6);
                                }
                                // faces is 0-indexed, activeFaceIndex is 1-indexed
                                var face = (_c = d.faces) === null || _c === void 0 ? void 0 : _c[d.activeFaceIndex - 1];
                                return (_f = (_e = (_d = face === null || face === void 0 ? void 0 : face.frontImage) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : String(d.activeFaceIndex);
                            });
                            setAnnounceText(t('text.rolledGroupAnnounce', { results: results.join(', ') }));
                        }, 1850);
                    }
                };
                // Touch devices: defer roll so SelectFocusOverlay click can cancel it
                if (IS_TOUCH_DEVICE) {
                    deferredRollTimer.current = setTimeout(doRoll, 100);
                }
                else {
                    doRoll();
                }
            }
            else {
                var _a = setStatesInManipulative({ x: groupPos.x, y: groupPos.y }, groupScale), oldItem = _a.oldItem, newItem = _a.newItem;
                var entry = {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(groupStartPos),
                    new: JSON.stringify(newItem),
                };
                if (!registerMultiSelectSaveEntries(id, [entry])) {
                    saveState(entry);
                }
            }
            setGroupStartPos([]);
            setIsDragging(false);
            isDraggingRef.current = false;
            setDragStart(null);
            dragStartRef.current = null;
            dragOffsetRef.current = null;
            groupDragStartPosRef.current = null;
            endMultiDrag();
        };
        if (dragStart !== null && isDragging && !panningEnable) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', stopDrag);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', stopDrag);
            window.addEventListener('touchcancel', stopDrag);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDrag);
            window.removeEventListener('touchcancel', stopDrag);
        };
    }, [
        isDragging,
        dragStart,
        JSON.stringify(groupPos),
        isFocused,
        panningEnable,
        groupScale,
        JSON.stringify(manipulative),
        derivedGroupScale,
    ]);
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var changeColour = function (data) {
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.type === 'dice' && item.data.groupId === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { color: data.colorKey }) }) : item;
                });
            });
        };
        var onToolClick = function (data) {
            switch (data.id) {
                case 'scaleMinus': {
                    var _minScale_1 = Math.max(0.5, groupScale - 0.1);
                    var _a = setStatesInManipulative({ x: groupPos.x, y: groupPos.y }, _minScale_1), minusOldItem = _a.oldItem, minusNewItem = _a.newItem;
                    setGroupScale(function (prev) { return _minScale_1; });
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(minusOldItem),
                        new: JSON.stringify(minusNewItem),
                    });
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        setAnnounceText(t('text.manipulativeScaled', {
                            name: t('text.dice'),
                            direction: t('text.down'),
                            value: _minScale_1.toFixed(1),
                        }));
                    });
                    break;
                }
                case 'scalePlus': {
                    var _plusScale_1 = groupScale + 0.1;
                    var _b = setStatesInManipulative({ x: groupPos.x, y: groupPos.y }, _plusScale_1), plusOldItem = _b.oldItem, plusNewItem = _b.newItem;
                    setGroupScale(function (prev) { return _plusScale_1; });
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(plusOldItem),
                        new: JSON.stringify(plusNewItem),
                    });
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        setAnnounceText(t('text.manipulativeScaled', {
                            name: t('text.dice'),
                            direction: t('text.up'),
                            value: _plusScale_1.toFixed(1),
                        }));
                    });
                    break;
                }
            }
        };
        var onRollDice = function (data) {
            if (data.id === id && data.isGroup) {
                // Roll all dice in the group via their imperative handles
                var oldDiceGroup_1 = [];
                var newDiceGroup_1 = [];
                Object.entries(manipulativeRef.current).forEach(function (_a) {
                    var _key = _a[0], value = _a[1];
                    if (_key.includes('dice')) {
                        value === null || value === void 0 ? void 0 : value.rollDice(function (_a) {
                            var oldDiceItem = _a.oldDiceItem, newDiceItem = _a.newDiceItem;
                            if (oldDiceItem && newDiceItem) {
                                oldDiceGroup_1.push(oldDiceItem);
                                newDiceGroup_1.push(newDiceItem);
                            }
                        });
                    }
                });
                if (newDiceGroup_1.length > 0) {
                    var newFaceMap_2 = new Map();
                    newDiceGroup_1.forEach(function (item) {
                        if (item.data.id)
                            newFaceMap_2.set(item.data.id, item);
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var _a;
                            var updated = newFaceMap_2.get((_a = item.data.id) !== null && _a !== void 0 ? _a : '');
                            return updated !== null && updated !== void 0 ? updated : item;
                        });
                    });
                }
                if (oldDiceGroup_1.length > 0 && newDiceGroup_1.length > 0) {
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldDiceGroup_1),
                        new: JSON.stringify(newDiceGroup_1),
                    });
                    setAnnounceText('');
                    setTimeout(function () {
                        var results = newDiceGroup_1.map(function (item) {
                            var _a, _b, _c, _d, _e, _f;
                            var d = item.data;
                            if (d.diceType === 'dot') {
                                return "face ".concat(d.activeFaceIndex, " of ").concat((_b = (_a = d.faces) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 6);
                            }
                            var face = (_c = d.faces) === null || _c === void 0 ? void 0 : _c[d.activeFaceIndex - 1];
                            return (_f = (_e = (_d = face === null || face === void 0 ? void 0 : face.frontImage) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : String(d.activeFaceIndex);
                        });
                        setAnnounceText(t('text.rolledGroupAnnounce', { results: results.join(', ') }));
                    }, 1850);
                }
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
            customEventHandler.addEventListener('colourChange', changeColour);
        }
        customEventHandler.addEventListener('rollDice', onRollDice);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
            customEventHandler.removeEventListener('colourChange', changeColour);
            customEventHandler.removeEventListener('rollDice', onRollDice);
        };
    }, [
        isFocused,
        groupScale,
        JSON.stringify(groupPos),
        JSON.stringify(manipulative),
        derivedGroupScale,
    ]);
    // When a group contains only patternShapes we remove the group background/padding
    // so the original shapes remain visually unchanged (no surrounding box).
    var groupContainsOnlyPatternShapes = data.every(function (item) { return item.type === 'patternShapes'; });
    // During exchange animation, some NMP pieces are fading out (animatingOut) while
    // new pieces fade in. Only pull animatingOut pieces out of flex layout when there
    // are also active (non-animatingOut) NMP pieces — otherwise the wrapper collapses.
    // Also require an in-flight NMP animation: a stale `animatingOut: true` left
    // behind by an interrupted flow should not change layout once the ticker
    // settles, otherwise that piece becomes permanently hidden.
    var hasActiveNmpPieces = isNmpAnimating &&
        data.some(function (d) {
            return (d.type === 'numberPiece' || d.type === 'moneyPiece') &&
                !d.data.animatingOut;
        });
    // Compute the flex container width from piece dimensions so the group background
    // fits tightly (no empty space). Uses the same wrapping logic as calcAbsolutePosition.
    var groupFlexWidth = useMemo(function () {
        var maxW = canvasWidth / 2;
        var rowW = 0;
        var widestRow = 0;
        var colCount = 0;
        var items = hasActiveNmpPieces
            ? data.filter(function (d) {
                return d.type === 'numberPiece' || d.type === 'moneyPiece'
                    ? !d.data.animatingOut
                    : true;
            })
            : data;
        var maxPerRow = getMaxItemsPerRow(items);
        items.forEach(function (d, idx) {
            var _a, _b, _c, _d, _e, _f, _g;
            var w = ((_f = (_c = (_b = (_a = d.data) === null || _a === void 0 ? void 0 : _a.defaultDimensions) === null || _b === void 0 ? void 0 : _b.width) !== null && _c !== void 0 ? _c : (_e = (_d = d.data) === null || _d === void 0 ? void 0 : _d.defaultDimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : 1) *
                ((_g = d.data.beforeGroupScale) !== null && _g !== void 0 ? _g : 1) *
                (groupScale !== null && groupScale !== void 0 ? groupScale : 1);
            var gap = idx < items.length - 1 ? GROUP_GAP : 0;
            // Wrap on width OR the per-row item cap (dice groups: 3 per row), matching
            // calcAbsolutePosition so the wrapper background fits the wrapped layout.
            if (idx > 0 && (rowW + w > maxW || colCount >= maxPerRow)) {
                widestRow = Math.max(widestRow, rowW - GROUP_GAP);
                rowW = 0;
                colCount = 0;
            }
            rowW += w + gap;
            colCount++;
        });
        widestRow = Math.max(widestRow, rowW);
        return widestRow + GROUP_PADDING * 2;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        JSON.stringify(data.map(function (item) {
            var _a, _b, _c, _d;
            return ({
                dw: (_b = (_a = item.data.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0,
                bs: (_c = item.data.beforeGroupScale) !== null && _c !== void 0 ? _c : 0,
                ao: item.type === 'numberPiece' || item.type === 'moneyPiece'
                    ? ((_d = item.data.animatingOut) !== null && _d !== void 0 ? _d : false)
                    : false,
            });
        })),
        groupScale,
        canvasWidth,
        hasActiveNmpPieces,
    ]);
    // Compute wrapper dimensions as the bounding box of children (accounts for various
    // sizes and positions). This ensures the wrapper covers children exactly and
    // avoids reflowing or misalignment due to flex layout.
    var groupDims = useMemo(function () {
        // Exclude pieces that are fading out (animatingOut) from layout calculation
        // so the group wrapper doesn't temporarily expand during exchange animations.
        // Only filter when there are active pieces to take their place.
        var activeData = hasActiveNmpPieces
            ? data.filter(function (d) {
                return d.type === 'numberPiece' || d.type === 'moneyPiece'
                    ? !d.data.animatingOut
                    : true;
            })
            : data;
        if (!activeData.length)
            return { width: 1, height: 1 };
        var minX = Infinity;
        var minY = Infinity;
        var maxX = -Infinity;
        var maxY = -Infinity;
        activeData.forEach(function (d) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
            var item = d.data;
            var w = ((_b = (_a = item.defaultDimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 60) *
                ((_c = item.beforeGroupScale) !== null && _c !== void 0 ? _c : 1) *
                ((_d = item.groupScale) !== null && _d !== void 0 ? _d : 1);
            var h = ((_f = (_e = item.defaultDimensions) === null || _e === void 0 ? void 0 : _e.height) !== null && _f !== void 0 ? _f : 60) *
                ((_g = item.beforeGroupScale) !== null && _g !== void 0 ? _g : 1) *
                ((_h = item.groupScale) !== null && _h !== void 0 ? _h : 1);
            var branch = d.type === 'patternShapes' ? 'topLeft-special-case' : 'center-anchored-default';
            if (d.type === 'patternShapes') {
                minX = Math.min(minX, (_k = (_j = item.center) === null || _j === void 0 ? void 0 : _j.x) !== null && _k !== void 0 ? _k : 0);
                minY = Math.min(minY, (_m = (_l = item.center) === null || _l === void 0 ? void 0 : _l.y) !== null && _m !== void 0 ? _m : 0);
                maxX = Math.max(maxX, ((_p = (_o = item.center) === null || _o === void 0 ? void 0 : _o.x) !== null && _p !== void 0 ? _p : 0) + w);
                maxY = Math.max(maxY, ((_r = (_q = item.center) === null || _q === void 0 ? void 0 : _q.y) !== null && _r !== void 0 ? _r : 0) + h);
            }
            else {
                minX = Math.min(minX, ((_t = (_s = item.center) === null || _s === void 0 ? void 0 : _s.x) !== null && _t !== void 0 ? _t : 0) - w / 2);
                minY = Math.min(minY, ((_v = (_u = item.center) === null || _u === void 0 ? void 0 : _u.y) !== null && _v !== void 0 ? _v : 0) - h / 2);
                maxX = Math.max(maxX, ((_x = (_w = item.center) === null || _w === void 0 ? void 0 : _w.x) !== null && _x !== void 0 ? _x : 0) + w / 2);
                maxY = Math.max(maxY, ((_z = (_y = item.center) === null || _y === void 0 ? void 0 : _y.y) !== null && _z !== void 0 ? _z : 0) + h / 2);
            }
            // eslint-disable-next-line no-console
            console.log('[groupDims debug] member contribution', {
                type: d.type,
                id: item.id,
                center: item.center,
                w: w,
                h: h,
                branchUsed: branch,
                // Dice is confirmed TOP-LEFT anchored elsewhere in the app (its own render uses
                // center as raw top/left, no -w/2 offset) — but this bounding-box calc only special
                // cases 'patternShapes' as top-left-anchored, so dice falls into the center-anchored
                // default branch. If that's wrong, dice's real footprint is center.x..center.x+w,
                // not center.x-w/2..center.x+w/2 as assumed here.
                likelyMisanchored: d.type === 'dice',
                runningBounds: { minX: minX, minY: minY, maxX: maxX, maxY: maxY },
            });
        });
        // eslint-disable-next-line no-console
        console.log('[groupDims debug] final bounds', { minX: minX, minY: minY, maxX: maxX, maxY: maxY });
        return {
            width: Math.max(1, Math.ceil(maxX - minX)),
            height: Math.max(1, Math.ceil(maxY - minY)),
        };
    }, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(data.map(function (item) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            return ({
                cx: (_b = (_a = item.data.center) === null || _a === void 0 ? void 0 : _a.x) !== null && _b !== void 0 ? _b : 0,
                cy: (_d = (_c = item.data.center) === null || _c === void 0 ? void 0 : _c.y) !== null && _d !== void 0 ? _d : 0,
                dw: (_f = (_e = item.data.defaultDimensions) === null || _e === void 0 ? void 0 : _e.width) !== null && _f !== void 0 ? _f : 0,
                dh: (_h = (_g = item.data.defaultDimensions) === null || _g === void 0 ? void 0 : _g.height) !== null && _h !== void 0 ? _h : 0,
                bs: (_j = item.data.beforeGroupScale) !== null && _j !== void 0 ? _j : 0,
                gs: (_k = item.data.groupScale) !== null && _k !== void 0 ? _k : 0,
                ao: item.type === 'numberPiece' || item.type === 'moneyPiece'
                    ? ((_l = item.data.animatingOut) !== null && _l !== void 0 ? _l : false)
                    : false,
            });
        })),
        groupScale,
        hasActiveNmpPieces,
    ]);
    /** ---------------- KEYBOARD HANDLER ---------------- */
    var onGroupKeyDown = function (e) {
        var _a;
        if (panningEnable)
            return;
        // Escape: deselect the group
        if (e.key === 'Escape') {
            e.preventDefault();
            setSelectedTool([]);
            return;
        }
        // Arrow key movement for the group
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            if (isOutlineGroup)
                return; // All members have positioning locked
            if (!keyMoveStartRef.current) {
                keyMoveStartRef.current = { x: groupPos.x, y: groupPos.y };
            }
            var step = e.shiftKey ? 20 : 5;
            var scaledStep = step / zoomLevel;
            var delta = {
                ArrowUp: { x: 0, y: -scaledStep },
                ArrowDown: { x: 0, y: scaledStep },
                ArrowLeft: { x: -scaledStep, y: 0 },
                ArrowRight: { x: scaledStep, y: 0 },
            };
            var d = delta[e.key];
            var newPos_1 = __assign(__assign({}, groupPos), { x: groupPos.x + d.x, y: groupPos.y + d.y });
            setGroupPos(newPos_1);
            setStatesInManipulative({ x: newPos_1.x, y: newPos_1.y }, groupScale);
            // Debounced undo save + screen reader announcement
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = setTimeout(function () {
                if (keyMoveStartRef.current) {
                    var _a = setStatesInManipulative({ x: newPos_1.x, y: newPos_1.y }, groupScale), oldItem = _a.oldItem, newItem = _a.newItem;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldItem),
                        new: JSON.stringify(newItem),
                    });
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        setAnnounceText(t('text.manipulativeMoved', {
                            name: groupLabel,
                            direction: e.key.replace('Arrow', '').toLowerCase(),
                            x: Math.round(newPos_1.x),
                            y: Math.round(newPos_1.y),
                        }));
                    });
                    keyMoveStartRef.current = null;
                }
            }, 500);
            return;
        }
        // Enter, Space, Shift+F10, ContextMenu: open context menu for the group
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10')) {
            e.preventDefault();
            if (!selectedTool.includes(id)) {
                setSelectedTool([id]);
            }
            var rect = (_a = groupInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: id,
                });
            }
        }
    };
    return (_jsxs("div", { ref: wrapperOuterRef, "data-manipulative-id": id, className: "manipulativeGroupWrapper ".concat(isFocused ? 'manipulativeGroupActive' : '', " "), style: (_a = {
                top: groupPos.y,
                left: groupPos.x,
                cursor: panningEnable ? 'inherit' : isDragging ? 'grabbing' : 'pointer'
            },
            _a['--scaleGroup'] = "".concat(groupScale),
            _a), children: [_jsx(SelectFocusOverlay, { id: id, title: groupLabel, ariaLabel: groupLabel, className: "groupSelectFocusState", onAfterClick: IS_TOUCH_DEVICE ? function () {
                    var _a;
                    // Cancel any deferred group roll triggered by VoiceOver's touch events
                    if (deferredRollTimer.current) {
                        clearTimeout(deferredRollTimer.current);
                        deferredRollTimer.current = null;
                    }
                    var rect = (_a = groupInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                    if (rect) {
                        setContextMenu({
                            x: rect.right,
                            y: rect.top,
                            manipulativeId: id,
                        });
                    }
                } : undefined }), _jsx("div", { ref: groupInteractiveRef, className: "manipulativeGroup", style: { flexWrap: 'wrap', width: groupFlexWidth }, onMouseDown: handleMouseDown, onTouchStart: handleMouseDown, role: "region", tabIndex: 0, "aria-label": groupAriaLabel, onKeyDown: onGroupKeyDown, 
                // Promote the group into selectedTool when keyboard focus lands here.
                // Without this, tabbing to the wrapper updates only document focus —
                // the `manipulativeGroupActive` glow is gated on selectedTool, so the
                // group looked unselected even though it had focus, and only the
                // single keyboard-focused child piece showed any highlight.
                onFocus: function () {
                    if (!selectedTool.includes(id))
                        setSelectedTool([id]);
                }, children: data.map(function (_manipulative, _index) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
                    var _manipulativeType = _manipulative.type;
                    switch (_manipulativeType) {
                        case 'dice':
                            var _manipulativeData_1 = _manipulative.data;
                            return (_jsx(DiceManipulative, { id: "".concat(_manipulativeData_1.id), type: _manipulativeData_1.type, diceType: _manipulativeData_1.diceType, center: _manipulativeData_1.center, scale: ((_a = _manipulativeData_1 === null || _manipulativeData_1 === void 0 ? void 0 : _manipulativeData_1.groupScale) !== null && _a !== void 0 ? _a : 1) *
                                    ((_b = _manipulativeData_1 === null || _manipulativeData_1 === void 0 ? void 0 : _manipulativeData_1.beforeGroupScale) !== null && _b !== void 0 ? _b : 1), ref: function (el) {
                                    manipulativeRef.current["".concat(_manipulativeData_1.id)] = el;
                                }, color: _manipulativeData_1.color, lock: _manipulativeData_1.lock, dimensions: _manipulativeData_1.dimensions, defaultDimensions: _manipulativeData_1.defaultDimensions, faces: _manipulativeData_1.faces, activeFaceIndex: _manipulativeData_1.activeFaceIndex, label: _manipulativeData_1.label, groupId: _manipulativeData_1.groupId, isGroup: true }, "".concat(_manipulativeData_1.id)));
                        case 'patternShapes': {
                            var _manipulativeData_2 = _manipulative.data;
                            // Render each pattern shape absolutely inside a full-size container so
                            // the parent's flex layout does not offset children. We still pass
                            // local center coords (absolute - groupPos) and keep child centers
                            // preserved when the group was created.
                            return (_jsx("div", { style: {
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    width: '100%',
                                    height: '100%',
                                }, children: _jsx(PatternShapesManipulative, { id: _manipulativeData_2.id, type: "patternShapes", shapeType: _manipulativeData_2.shapeType, center: {
                                        x: ((_d = (_c = _manipulativeData_2.center) === null || _c === void 0 ? void 0 : _c.x) !== null && _d !== void 0 ? _d : 0) - ((_e = groupPos.x) !== null && _e !== void 0 ? _e : 0),
                                        y: ((_g = (_f = _manipulativeData_2.center) === null || _f === void 0 ? void 0 : _f.y) !== null && _g !== void 0 ? _g : 0) - ((_h = groupPos.y) !== null && _h !== void 0 ? _h : 0),
                                    }, rotation: _manipulativeData_2.rotation, scale: ((_j = _manipulativeData_2 === null || _manipulativeData_2 === void 0 ? void 0 : _manipulativeData_2.groupScale) !== null && _j !== void 0 ? _j : 1) *
                                        ((_k = _manipulativeData_2 === null || _manipulativeData_2 === void 0 ? void 0 : _manipulativeData_2.beforeGroupScale) !== null && _k !== void 0 ? _k : 1), color: _manipulativeData_2.color, lock: _manipulativeData_2.lock, isGroup: true, path: _manipulativeData_2.path, groupId: _manipulativeData_2.groupId }) }, _manipulativeData_2.id));
                        }
                        case 'counter':
                            var _counterData_1 = _manipulative.data;
                            var counterScale = ((_l = _counterData_1 === null || _counterData_1 === void 0 ? void 0 : _counterData_1.groupScale) !== null && _l !== void 0 ? _l : 1) * ((_m = _counterData_1 === null || _counterData_1 === void 0 ? void 0 : _counterData_1.beforeGroupScale) !== null && _m !== void 0 ? _m : 1);
                            var counterW = ((_p = (_o = _counterData_1.defaultDimensions) === null || _o === void 0 ? void 0 : _o.width) !== null && _p !== void 0 ? _p : 50) * counterScale;
                            var counterH = ((_r = (_q = _counterData_1.defaultDimensions) === null || _q === void 0 ? void 0 : _q.height) !== null && _r !== void 0 ? _r : 50) * counterScale;
                            // Render counter shape inline for group layout
                            var renderGroupCounterShape = function () {
                                var shape = _counterData_1.shape, color = _counterData_1.color;
                                // numberFrame token shapes (shape0-shape7)
                                if (typeof shape === 'string' && shape.startsWith('shape')) {
                                    var svgPath = "./assets/images/manipulative/numberFrames/".concat(color, "/").concat(shape, "/1.svg");
                                    return (_jsx("img", { src: getImageSrc(svgPath), alt: "".concat(shape, " token"), style: { width: '100%', height: '100%', objectFit: 'contain' }, draggable: "false" }));
                                }
                                var shapeStyle = {
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: color,
                                };
                                switch (shape) {
                                    case 'circle':
                                        return _jsx("div", { style: __assign(__assign({}, shapeStyle), { borderRadius: '50%' }) });
                                    case 'square':
                                        return _jsx("div", { style: shapeStyle });
                                    case 'star':
                                        return (_jsx("div", { style: shapeStyle, children: _jsx("svg", { viewBox: "0 0 24 24", fill: color, style: { width: '100%', height: '100%' }, children: _jsx("path", { d: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" }) }) }));
                                    default:
                                        return _jsx("div", { style: __assign(__assign({}, shapeStyle), { borderRadius: '50%' }) });
                                }
                            };
                            return (_jsx("div", { style: {
                                    width: counterW,
                                    height: counterH,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transform: "rotate(".concat((_s = _counterData_1.rotation) !== null && _s !== void 0 ? _s : 0, "deg)"),
                                }, children: renderGroupCounterShape() }, _counterData_1.id));
                        case 'numberPiece':
                        case 'moneyPiece': {
                            var _nmpData = _manipulative.data;
                            // Skip rendering animatingOut pieces once active (new) pieces exist.
                            // By this point the old piece's CSS fade-out (0.3s) has already completed
                            // (Phase 2 fires at ~400ms), so hiding it causes no visual change and
                            // prevents the group from expanding to fit both old + new pieces.
                            // `hasActiveNmpPieces` already requires `isNmpAnimating`, so a
                            // stale `animatingOut: true` left over from an interrupted flow
                            // won't hide the piece once the ticker settles.
                            if (_nmpData.animatingOut && hasActiveNmpPieces) {
                                return null;
                            }
                            return (_jsx(NumberAndMoneyPieceManipulative, { data: _nmpData, isGroup: true }, _nmpData.id));
                        }
                        default:
                            // To add a new groupable type:
                            // 1. Add a case here rendering the component with isGroup={true}
                            // 2. Add type to GROUP_TYPE_RIGHT_CONFIG in src/helper/groupHelper.ts
                            // 3. Remove 'group' from TOOL_RULES hiddenElements in GameController.tsx
                            // 4. Handle isGroup prop in the manipulative component (disable drag, etc.)
                            return null;
                    }
                }) }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '0.0625rem',
                    height: '0.0625rem',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText })] }));
};
export default ManipulativeGroup;
